import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../redux/store';
import { updateUserLastLogin } from '../redux/slices/user';

/**
 * Safety-net interval for a tab that is left open and visible across a day
 * boundary without any navigation or focus change. Deliberately slow — the
 * ping itself is date-gated, so this rarely results in a real request.
 */
const HEARTBEAT_MS = 5 * 60 * 1000;

/** localStorage key holding the last UTC date (YYYY-MM-DD) we pinged for a user. */
const streakKey = (userId: string) => `streakPing:${userId}`;

/** Today's calendar day in UTC, matching how the backend keys the streak day. */
const todayUtc = (): string => new Date().toISOString().slice(0, 10);

/**
 * Keeps a user's login streak alive regardless of whether they log in fresh
 * each day or simply stay logged in and revisit the app.
 *
 * The backend ({@link updateUserLastLogin} → `lastLoggedIn`) is idempotent per
 * UTC calendar day: a repeat call on the same day leaves the streak unchanged,
 * a call the next consecutive day increments it, and a gap resets it. So the
 * only job here is to ensure the endpoint is hit at least once on each new UTC
 * day the user is active.
 *
 * We record the last date we pinged in localStorage (per user, survives reloads
 * and new sessions on the device) and re-check on every event that signals the
 * user is present — mount, route change, tab focus/visibility — plus a slow
 * interval as a fallback for an idle-but-open tab. A ping only fires when the
 * stored date differs from today's UTC date, so same-day revisits are no-ops.
 *
 * Mount once, app-wide, inside the authenticated layout.
 */
export const useDailyLoginStreak = (): void => {
	const dispatch = useAppDispatch();
	const location = useLocation();
	const userId = useAppSelector((state) => state.user.currentUser?.id);

	// In-memory guard so multiple triggers firing in the same tick don't race to
	// dispatch before localStorage/redux settle. Keyed by user+day so switching
	// accounts within the same tab (the tracker stays mounted, so this ref
	// survives the switch) still pings each account once per UTC day.
	const lastPingedRef = useRef<string | null>(null);

	const maybePing = useCallback(() => {
		if (!userId) return;
		if (document.visibilityState === 'hidden') return;

		const today = todayUtc();
		const guard = `${userId}:${today}`;
		if (lastPingedRef.current === guard) return;

		let stored: string | null = null;
		try {
			stored = localStorage.getItem(streakKey(userId));
		} catch {
			// Private-mode / storage-disabled: fall back to in-memory guard only.
		}
		if (stored === today) {
			lastPingedRef.current = guard;
			return;
		}

		// Record before dispatching so a concurrent trigger sees the new date and
		// skips. The backend dedupes per day anyway, so a rare double call is safe.
		lastPingedRef.current = guard;
		try {
			localStorage.setItem(streakKey(userId), today);
		} catch {
			// ignore write failures; the in-memory guard still prevents spam.
		}
		dispatch(updateUserLastLogin({ id: userId, lastLoggedIn: new Date().toISOString() }));
	}, [dispatch, userId]);

	// Fire on mount, whenever the user changes, and on every navigation.
	useEffect(() => {
		maybePing();
	}, [maybePing, location.pathname]);

	// Fire when the tab regains focus or becomes visible again (covers a tab that
	// was backgrounded across midnight), plus a slow visible-only heartbeat.
	useEffect(() => {
		if (!userId) return undefined;
		window.addEventListener('focus', maybePing);
		document.addEventListener('visibilitychange', maybePing);
		const intervalId = window.setInterval(maybePing, HEARTBEAT_MS);
		return () => {
			window.removeEventListener('focus', maybePing);
			document.removeEventListener('visibilitychange', maybePing);
			window.clearInterval(intervalId);
		};
	}, [maybePing, userId]);
};
